import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { urlFor } from "@/sanity/lib/image";
import InlineSVG from "@/components/InlineSVG";

const Swiper = dynamic(() => import('swiper/react').then(mod => mod.Swiper), { ssr: false });
import { SwiperSlide } from 'swiper/react';
import { Navigation } from "swiper/modules";

import styles from "./brandSlider.module.scss";
import Button from "@/components/Button";

import ArrowRight from "/packages/icons/arrow-right.svg";
import ArrowLeft from "/packages/icons/arrow-left.svg";
import BrandsIcon from "/packages/icons/category/brands.svg";

export default function BrandSlider({ title = 'Brands', brands = [], viewAllLink = '/brands' }) {
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    const handleInteraction = () => {
      setHasInteracted(true);
      interactionEvents.forEach(event => window.removeEventListener(event, handleInteraction));
    };

    const interactionEvents = ['click', 'touchstart', 'keydown', 'scroll'];
    interactionEvents.forEach(event =>
      window.addEventListener(event, handleInteraction, { once: true })
    );

    return () => {
      interactionEvents.forEach(event =>
        window.removeEventListener(event, handleInteraction)
      );
    };
  }, []);

  const swiperConfig = {
    modules: [Navigation],
    className: styles.brandSlider,
    touchEventsTarget: "wrapper",
    breakpoints: {
      1024: { slidesPerView: 5, slidesPerGroup: 5, spaceBetween: 16 },
      768: { slidesPerView: 4, slidesPerGroup: 4, spaceBetween: 14 },
      550: { slidesPerView: 3, slidesPerGroup: 3, spaceBetween: 10 },
      0: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 10 },
    },
    navigation: {
      prevEl: `#brand-prev`,
      nextEl: `#brand-next`,
      clickable: true,
    },
  };

  return (
    <div className={`${styles.container} brands-slider`}>
      <div className={styles.header}>
        <div className={styles.header__content}>
          <div className={styles.title}>
            <h2>{title}</h2>
          </div>
        </div>
        <Button
          type="link"
          href={viewAllLink}
          className="sliderLink"
          prefetch={false}
          ariaLabel="View All Brands"
        >
          <span>view all</span>
        </Button>
      </div>

      <Swiper {...swiperConfig}>
        <div className={styles.prev} id="brand-prev">
          <Button type="button" className="slider-arrow" ariaLabel="Previous Slide">
            <ArrowLeft />
          </Button>
        </div>
        <div className={styles.next} id="brand-next">
          <Button type="button" className="slider-arrow" ariaLabel="Next Slide">
            <ArrowRight />
          </Button>
        </div>

        {brands.map((brand, i) => {
          if (!brand.logo) return null;

          return (
            <SwiperSlide key={i} className={styles.slide}>
              <Link
                href={brand.link || "#"}
                passHref
                style={{
                  backgroundColor: brand.backgroundColor?.hex || "#fff",
                  color: brand.textColor || "#000",
                }}
                aria-label={brand.name}
              >
                {hasInteracted ? (
                  <InlineSVG
                    url={urlFor(brand.logo).url()}
                    color={brand.logoColor?.hex || "#000"}
                    className={styles.icon}
                  />
                ) : (
                  <div className={styles.icon} style={{ height: "80px", width: "100%" }} />
                )}
              </Link>
            </SwiperSlide>
          );
        })}

        <SwiperSlide className={styles.lastSlide}>
          <Link href={viewAllLink}>
            <div className={styles.icon}>
              <BrandsIcon />
            </div>
            View All
          </Link>
        </SwiperSlide>
      </Swiper>
    </div>
  );
}
